(() => {
  const COMPONENT_NAME = 'volumental-measurement';

  function waitForWidget() {
    if (window.customElements?.get(COMPONENT_NAME)) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new Error('The Volumental scanner could not be loaded.'));
      }, 10000);

      window.customElements.whenDefined(COMPONENT_NAME).then(() => {
        window.clearTimeout(timeout);
        resolve();
      });
    });
  }

  async function validateSession(serviceUrl, sessionId) {
    const response = await fetch(`${serviceUrl}/api/fizo/scan-sessions/${encodeURIComponent(sessionId)}`, {
      headers: {accept: 'application/json'},
    });
    if (!response.ok) throw new Error('This scan link has expired. Please start again from My Foot File.');
    return response.json();
  }

  async function submitScanResult(serviceUrl, scanSession, scanId) {
    const response = await fetch(`${serviceUrl}/api/fizo/scan-results`, {
      method: 'POST',
      headers: {'content-type': 'application/json', accept: 'application/json'},
      body: JSON.stringify({scanSession, scanId}),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload) throw new Error('Your scan was completed, but the fit profile could not be saved. Please retry later.');
    return payload;
  }

  async function initialize(root) {
    const mount = root.querySelector('[data-fizo-scan-widget]');
    const status = root.querySelector('[data-fizo-scan-status]');
    const notice = root.querySelector('[data-fizo-scan-notice]');
    const returnLink = root.querySelector('[data-fizo-scan-return]');
    const query = new URLSearchParams(window.location.search);
    const requestedReturnUrl = query.get('return_to');
    const scanSession = query.get('s');
    // A session return target is authoritative. Until it is loaded, preserve an
    // explicit query target before falling back to the section's generic default.
    const returnUrl = requestedReturnUrl || root.dataset.returnUrl;
    let scanOpened = false;
    let scanSaved = false;

    const confirmLeave = (event) => {
      if (!scanOpened || scanSaved) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', confirmLeave);

    if (returnUrl) returnLink.href = returnUrl;

    try {
      if (!scanSession) {
        status.textContent = 'Please start your scan from My Foot File so we can save it to your account.';
        return;
      }

      status.textContent = 'Preparing your secure scan...';
      const session = await validateSession(root.dataset.scanServiceUrl, scanSession);
      if (session.returnUrl) returnLink.href = session.returnUrl;

      status.textContent = 'Loading scanner...';
      await waitForWidget();

      const widget = document.createElement(COMPONENT_NAME);
      widget.setAttribute('locale', document.documentElement.lang || 'en');
      widget.setAttribute('measurement-on-widget', 'true');
      widget.setAttribute('theme', 'light');
      widget.setAttribute('variant', 'button');

      widget.addEventListener('volumental:on-opened', () => {
        scanOpened = true;
        status.textContent = 'Complete this new scan to add a profile to your Scan History.';
      });

      widget.addEventListener('volumental:on-error', () => {
        status.textContent = 'The scan could not be completed. Please try again.';
      });

      widget.addEventListener('volumental:on-measurement', async (event) => {
        const scanId = event?.detail?.id;
        if (!scanId) return;

        status.textContent = 'Reading your fit profile...';
        try {
          const completion = await submitScanResult(root.dataset.scanServiceUrl, scanSession, scanId);
          if (completion.alreadyImported) {
            status.textContent = 'This scan has already been saved. Start and complete a new foot scan to create another profile.';
            if (notice) notice.hidden = false;
            returnLink.hidden = false;
            return;
          }
          scanSaved = true;
          status.textContent = completion.recommendedSizeText || 'Your scan has been saved to My Foot File.';
          if (notice) notice.hidden = true;
          returnLink.hidden = false;
        } catch (error) {
          status.textContent = error instanceof Error ? error.message : 'We could not read this scan. Please try again.';
        }
      });

      mount.replaceChildren(widget);
      status.textContent = '';
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : 'The scanner is temporarily unavailable.';
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-fizo-scan]').forEach(initialize);
  });
})();
